import { Bookmark, Edit, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useDeletePromptPreset,
  usePromptPresets,
  usePromptTemplates,
  type PromptPresetRecord,
} from '@/hooks/usePromptData';

import type { PromptScope } from '@goenhub/shared';

import { PresetFormDialog } from './PresetFormDialog';

type ScopeFilter = 'all' | PromptScope;

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; preset: PromptPresetRecord };

const SCOPE_LABEL: Record<PromptScope, string> = {
  mail: 'メール',
  line_news: 'LINE NEWS',
  both: '両方',
};

export function PromptPresetsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data: presets, isLoading } = usePromptPresets();
  const { data: templates } = usePromptTemplates();
  const deleteMutation = useDeletePromptPreset();
  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' });
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');

  const templateMap = new Map(templates?.map((t) => [t.id, t.name]));

  const filtered = useMemo(() => {
    if (!presets) return [];
    if (scopeFilter === 'all') return presets;
    return presets.filter((p) => p.scope === scopeFilter);
  }, [presets, scopeFilter]);

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`プレセット「${name}」を削除します。よろしいですか？`)) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const closeDialog = () => setDialog({ mode: 'closed' });

  return (
    <Screen
      title="プレセット管理"
      description="メール生成 / LINE NEWS 生成の入力値セット"
      actions={
        canEdit && templates && templates.length > 0 ? (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => setDialog({ mode: 'create' })}
          >
            <Plus />
            新規作成
          </Button>
        ) : undefined
      }
    >
      <Tabs value={scopeFilter} onValueChange={(v) => setScopeFilter(v as ScopeFilter)}>
        <TabsList>
          <TabsTrigger value="all">すべて</TabsTrigger>
          <TabsTrigger value="mail">メール</TabsTrigger>
          <TabsTrigger value="line_news">LINE NEWS</TabsTrigger>
          <TabsTrigger value="both">両方</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <Spinner />
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4"
            >
              <div className="flex items-start gap-2 mb-2">
                <Bookmark className="size-5 text-[var(--accent)] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">{p.name}</h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="default">
                      {templateMap.get(p.templateId) ?? p.templateId}
                    </Badge>
                    <Badge variant="default">{SCOPE_LABEL[p.scope]}</Badge>
                  </div>
                </div>
              </div>
              <div className="text-xs text-[var(--text-muted)]">
                入力値: {Object.keys(p.values ?? {}).length} 件
              </div>
              {canEdit && (
                <div className="flex justify-end gap-1 pt-3 border-t border-[var(--border)] mt-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setDialog({ mode: 'edit', preset: p })}
                  >
                    <Edit />
                    編集
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(p.id, p.name)}
                  >
                    <Trash2 />
                    削除
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Bookmark}
          title="プレセットがまだありません"
          description={
            templates && templates.length > 0
              ? '「新規作成」ボタン、または各生成画面の「プレセットとして保存」から作成できます'
              : 'まずテンプレートを登録してからプレセットを作成してください'
          }
        />
      )}

      <PresetFormDialog
        open={dialog.mode !== 'closed'}
        onOpenChange={(o) => !o && closeDialog()}
        initial={dialog.mode === 'edit' ? dialog.preset : undefined}
      />
    </Screen>
  );
}
